import { useCallback, useEffect, useState } from 'react';
import {
  PUSH_SUPPORT,
  currentSubscription,
  disablePush,
  enablePush,
  hasPushApis,
  supportState,
} from '../../Services/PushNotifications';

/**
 * "Are notifications on for this phone, and may they be?" — the whole state
 * machine, in one place.
 *
 * Shared by the driver portal's two entry points (the enrolment screen and
 * the settings card) so the two can never disagree about what this device
 * can do. The dashboard's settings screen uses the same service underneath
 * but manages a LIST of devices, which is a different shape, so it does not
 * use this hook.
 *
 * WHAT IT NEVER DOES
 * ------------------
 * It never reports READY on a device that cannot deliver. `supportState`
 * distinguishes "your iPhone needs the Home Screen icon", "you said no and
 * only you can undo that", "this browser has no push at all" and "the server
 * has no keys", and the screens say something true for each. That is the
 * whole reason this returns a `support` string rather than a boolean.
 *
 * `enable` MUST be called from a click handler: browsers refuse a permission
 * prompt outside a user gesture, silently on iOS.
 */
export default function usePushSetting(api) {
  const [support, setSupport] = useState(PUSH_SUPPORT.UNSUPPORTED);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Whether THIS browser holds a subscription is a local question and is
      // answered first: a device that is subscribed is "on" even if the
      // network is down.
      const subscription = await currentSubscription();
      // The server is only asked whether push is configured at all. A
      // failure here (offline, signed out) must not claim push is broken, so
      // it falls back to "no key known", which reads as "not available".
      const state = hasPushApis()
        ? await api.getState().catch(() => ({ public_key: undefined }))
        : { public_key: undefined };

      setSupport(supportState({ publicKey: state.public_key, subscribed: !!subscription }));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let live = true;

    refresh().catch(() => {
      if (live) setLoading(false);
    });

    return () => { live = false; };
  }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const result = await enablePush(api);

      setSupport(result.ok ? PUSH_SUPPORT.SUBSCRIBED : (result.reason || PUSH_SUPPORT.READY));

      return result;
    } catch (error) {
      // A refused permission is not an error; a network failure is. Either
      // way the switch goes back to where it was rather than lying.
      await refresh().catch(() => undefined);

      return { ok: false, error };
    } finally {
      setBusy(false);
    }
  }, [api, refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await disablePush(api);
      await refresh();

      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    } finally {
      setBusy(false);
    }
  }, [api, refresh]);

  return {
    support,
    loading,
    busy,
    isOn: support === PUSH_SUPPORT.SUBSCRIBED,
    /** True only when asking would actually produce a prompt. */
    canAsk: support === PUSH_SUPPORT.READY,
    enable,
    disable,
    refresh,
  };
}
